/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	FileText,
	ShoppingCart,
	CreditCard,
	Truck,
	Code,
	Shield,
	AlertTriangle,
	MessageSquare,
	Info,
	UserX,
	RefreshCw, // Added UserX for conduct
} from "lucide-react"; // Refined icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// --- Structured Terms Data ---
// (Keep legal text accurate, structure for readability)
const termsData = {
	lastUpdated: "October 27, 2023", // Add Last Updated Date
	introduction: {
		title: "Welcome to Off Cut Shades",
		text: [
			"These Terms and Conditions outline the rules and regulations for the use of Off Cut Shades' Website, located at offcutshades.com.",
			"By accessing this website and/or placing an order, we assume you accept these terms and conditions in full. Do not continue to use Off Cut Shades if you do not accept all of the terms stated on this page.",
			"Please read these carefully as they affect your legal rights.",
		],
	},
	sections: [
		{
			id: "orders",
			icon: ShoppingCart,
			title: "Placing & Accepting Orders",
			content: [
				{
					text: "Receiving an order confirmation (electronic or other) after you submit an order does not signify our final acceptance of your order, nor does it constitute confirmation of our offer to sell. We reserve the right at any time after receipt of your order to accept or decline your order for any reason.",
				},
				{
					text: "We may require additional verification or information before accepting any order.",
				},
			],
		},
		{
			id: "payment",
			icon: CreditCard,
			title: "Payment Terms",
			content: [
				{
					text: "All payments for orders placed through the Site must be made using a valid payment method accepted by us (e.g., Credit Card via Stripe, PayPal, Affirm where available).",
				},
				{
					text: "You are responsible for providing accurate payment information and ensuring sufficient funds are available. You are also responsible for any fees charged by your card issuer or payment provider.",
				},
			],
		},
		{
			id: "shipping",
			icon: Truck,
			title: "Shipping & Delivery",
			content: [
				{
					text: (
						<>
							We strive to process and ship orders quickly (typically within 2
							business days). Estimated delivery times provided are estimates
							only, starting from the date of shipment. See our full{" "}
							<Link
								to="/help/shipping-policy"
								className="text-primary hover:underline font-medium"
							>
								Shipping Policy
							</Link>{" "}
							for detailed information on costs, locations, and estimated times.
						</>
					),
				},
				{
					text: "We cannot guarantee delivery by a specific date and are not liable for carrier delays. Title and risk of loss pass to you once the order is delivered to the shipping carrier.",
				},
			],
		},
		{
			id: "ip",
			icon: Code, // Or Copyright icon
			title: "Intellectual Property",
			content: [
				{
					text: "All content included on the Site, such as text, graphics, logos, images, as well as the compilation thereof, and any software used on the Site, is the property of Off Cut Shades or its suppliers and protected by copyright and other intellectual property laws.",
				},
				{
					text: "You agree not to copy, reproduce, distribute, transmit, display, sell, license, or otherwise exploit any content for any commercial purposes without our express prior written consent.",
				},
			],
		},
		{
			id: "conduct",
			icon: UserX, // Icon indicating prohibited user actions
			title: "Website Use & User Conduct",
			content: [
				{
					text: "You agree to use the Site only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the Site. Prohibited behavior includes:",
					list: [
						"Damaging, disabling, overburdening, or impairing the Site.",
						"Attempting to gain unauthorized access to any systems or networks.",
						"Transmitting spam, viruses, or other harmful code.",
						"Using the Site to impersonate any person or entity.",
						"Collecting information about others without their consent.",
						"Interfering with any other party's use of the Site.",
					],
				},
			],
		},
		{
			id: "disclaimer",
			icon: AlertTriangle,
			title: "Disclaimer of Warranties & Limitation of Liability",
			content: [
				{
					text: 'The Site and all information, content, materials, and products included on or otherwise made available to you through the Site are provided by Off Cut Shades on an **"as is"** and **"as available"** basis, unless otherwise specified in writing.',
				},
				{
					text: "Off Cut Shades makes no representations or warranties of any kind, express or implied, as to the operation of the Site or the information, content, materials, or products included on the Site. We do not warrant that the Site will be uninterrupted or error-free.",
				},
				{
					text: "To the full extent permissible by applicable law, Off Cut Shades disclaims all warranties, express or implied, including, but not limited to, implied warranties of merchantability and fitness for a particular purpose.",
				},
				{
					text: "Off Cut Shades will not be liable for any damages of any kind arising from the use of the Site or from any information, content, materials, or products included on or otherwise made available to you through the Site, unless otherwise specified in writing.",
				},
			],
		},
		{
			id: "changes",
			icon: RefreshCw,
			title: "Changes to Terms",
			content: [
				{
					text: `We reserve the right to modify these Terms and Conditions at any time. Changes and clarifications will take effect immediately upon their posting on the website. We encourage you to review this page periodically for updates. Your continued use of the site after changes constitutes acceptance.`,
				},
			],
		},
		{
			id: "contact",
			icon: MessageSquare,
			title: "Questions About These Terms",
			content: [
				{
					text: (
						<>
							If you have any questions regarding these Terms and Conditions,
							please contact us through our{" "}
							<Link
								to="/contact"
								className="text-primary hover:underline font-medium"
							>
								Contact Us
							</Link>{" "}
							page.
						</>
					),
				},
			],
		},
	],
};

// --- Main Terms and Conditions Screen Component ---
const TermsAndConditions = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			{" "}
			{/* Standard bg */}
			<Helmet>
				<title>Terms and Conditions | Off Cut Shades</title>
				<meta
					name="description"
					content="Read the official Terms and Conditions for using the Off Cut Shades website and purchasing custom window shades and blinds."
				/>{" "}
				{/* Updated Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/help/terms-conditions"
				/>
				<meta name="robots" content="noindex, follow" />
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<FileText size={36} strokeWidth={1.5} /> {/* Main Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Terms & Conditions
					</motion.h1>
					{termsData.lastUpdated && (
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-sm text-gray-500"
						>
							Last Updated: {termsData.lastUpdated}
						</motion.p>
					)}
				</section>
				{/* --- Introduction Section --- */}
				<section className="container mx-auto px-4 max-w-3xl mx-auto mb-10 md:mb-12">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5 }}
						className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 space-y-3 text-base text-gray-700 leading-relaxed"
					>
						<h2 className="text-xl font-semibold text-gray-800 mb-3">
							{termsData.introduction.title}
						</h2>
						{termsData.introduction.text.map((paragraph, idx) => (
							<p key={`intro-${idx}`}>{paragraph}</p>
						))}
					</motion.div>
				</section>
				{/* --- Terms Sections --- */}
				<section className="container mx-auto px-4 max-w-3xl mx-auto">
					<div className="space-y-6 md:space-y-8">
						{" "}
						{/* Slightly reduced spacing */}
						{termsData.sections.map((section, index) => (
							<motion.div
								key={section.id}
								className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" // Reduced shadow
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, amount: 0.2 }}
								transition={{ duration: 0.5, delay: index * 0.05 }} // Subtle delay
							>
								{/* Section Header */}
								<div className="p-5 md:p-6 border-b border-gray-100">
									<div className="flex items-center gap-3">
										<div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
											{React.createElement(section.icon, {
												size: 20,
												strokeWidth: 2,
											})}
										</div>
										<h2 className="text-lg md:text-xl font-semibold text-gray-800">
											{section.title}
										</h2>
									</div>
								</div>
								{/* Section Content */}
								<div className="p-5 md:p-6 space-y-3 text-sm text-gray-700 leading-relaxed">
									{section.content.map((contentItem, cIndex) => (
										<div key={`${section.id}-${cIndex}`}>
											{/* Main Text */}
											{contentItem.text && (
												<p className="mb-2 last:mb-0">
													{typeof contentItem.text === "string" ? (
														<span
															dangerouslySetInnerHTML={{
																__html: contentItem.text.replace(
																	/\*\*(.*?)\*\*/g,
																	"<strong>$1</strong>"
																),
															}}
														/>
													) : (
														contentItem.text
													)}
												</p>
											)}
											{/* Bullet List */}
											{contentItem.list && (
												<ul className="list-disc list-outside space-y-1.5 pl-5 mt-2">
													{contentItem.list.map((item, itemIndex) => (
														<li key={itemIndex}>{item}</li>
													))}
												</ul>
											)}
										</div>
									))}
								</div>
							</motion.div>
						))}
					</div>
				</section>
				{/* --- Contact Block --- */}
				{/* Re-using ContactBlock, tailor props if needed */}
				<ContactBlock
					title="Questions About Our Terms?"
					description="If any part of our Terms and Conditions is unclear, please feel free to reach out to us for clarification."
				/>
			</main>
		</div>
	);
};

export default TermsAndConditions;
